import ModalBackdrop from '@map/components/ModalBackdrop';
import ModalContent from '@map/components/ModalContent';
import { makeStyles } from '@map/theme';
import { Slide, Typography } from '@mui/material';
import { useCurrentLocale } from '@shared/hooks/intl';
import { getLocaleDirection } from '@shared/util/locale';
import Button from '@ui/Button';
import ArrowBackIcon from '@ui/Icon/library/ArrowBackIcon';
import CloseIcon from '@ui/Icon/library/CloseIcon';
import { ReactNode } from 'react';

const useStyles = makeStyles(({ spacing, palette }) => ({
  popover: {
    flexGrow: 1,
    background: palette.common.white,
    padding: spacing(8.5, 8, 6, 8),
    borderTopLeftRadius: spacing(6),
    borderTopRightRadius: spacing(6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  prompt: {
    fontSize: '1.125rem',
    fontWeight: 700,
    lineHeight: '1.5rem',
    width: '100%',
  },
  closeButton: {
    marginTop: spacing(4),
  },
  rightContent: {
    borderTopLeftRadius: spacing(6),
    borderTopRightRadius: 0,
    borderBottomLeftRadius: spacing(6),
  },
  promptLine: {
    display: 'flex',
    alignSelf: 'flex-start',
    marginBottom: spacing(4),
  },
  arrow: {
    marginRight: spacing(2),
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  prompt?: ReactNode;
  actionButton?: ReactNode;
  className?: string;
  closeOnBackdrop?: boolean;
  position?: 'bottom' | 'right';
  showArrow?: ReactNode;
  children: ReactNode;
};

const PullupModal = ({
  open,
  prompt,
  onClose,
  actionButton,
  position = 'bottom',
  className,
  children,
  closeOnBackdrop = true,
  showArrow,
}: Props) => {
  const { classes, cx } = useStyles();
  const currentLocale = useCurrentLocale();
  const localeDirection = getLocaleDirection(currentLocale);

  const direction = () => {
    if (position === 'bottom') {
      return 'up';
    }
    return localeDirection === 'rtl' ? 'right' : 'left';
  };

  return (
    <>
      {open && <ModalBackdrop onClick={closeOnBackdrop ? onClose : undefined} />}
      <ModalContent position={position}>
        <Slide direction={direction()} in={open} mountOnEnter unmountOnExit>
          <div
            className={cx(classes.popover, position === 'right' && classes.rightContent, className)}
          >
            {(showArrow || prompt) && (
              <div className={classes.promptLine}>
                {showArrow && (
                  <Button
                    className={classes.arrow}
                    icon={true}
                    size="medium"
                    variant="tertiary"
                    onClick={onClose}
                  >
                    <ArrowBackIcon />
                  </Button>
                )}
                {prompt && <Typography className={classes.prompt}>{prompt}</Typography>}
              </div>
            )}
            {children}

            {actionButton === 'close' ? (
              <Button
                icon={true}
                size="medium"
                onClick={onClose}
                className={classes.closeButton}
                ariaLabelIntlKey="page_nav.cancel_button"
              >
                <CloseIcon />
              </Button>
            ) : (
              actionButton
            )}
          </div>
        </Slide>
      </ModalContent>
    </>
  );
};

export default PullupModal;
